import React, { useEffect, useRef, useState } from 'react';
import { Shield, Clock, Truck, Users, Award, MapPin } from 'lucide-react';
import { whyChooseUs } from '../utils/mock';

const iconMap = {
  shield: Shield,
  clock: Clock,
  truck: Truck,
  users: Users
};

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 md:py-32 relative overflow-hidden">
      {/* Clean Gradient Background - Perfect for Text Readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FDFCFB] via-white to-[#FFF8F0]"></div>

      {/* Subtle Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-gradient-to-br from-[#E6A05C]/10 to-transparent rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-gradient-to-tl from-[#D4894B]/10 to-transparent rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* About Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left - Text Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center space-x-2 bg-[#E6A05C]/15 border-2 border-[#E6A05C]/30 rounded-full px-6 py-2 mb-6">
              <Award className="text-[#D4894B]" size={16} />
              <span className="text-[#0D2436] font-bold text-sm uppercase tracking-wide">About Us</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D2436] mb-6">
              Moving America Forward, One Mile at a Time
            </h2>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Founded in 2024, Vitanza Logistics brings fresh energy and innovative solutions to the transportation industry. We combine modern technology with old-school reliability to provide exceptional logistics services across North America.
            </p>
            
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Our mission is simple: deliver your cargo safely, on time, every time. Whether it's cross-border shipments, temperature-controlled freight, or specialized logistics, we have the expertise and infrastructure to handle it all.
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-white to-[#FAF7F3] rounded-xl p-6 shadow-lg border-2 border-[#E6A05C]/20 hover:border-[#E6A05C]/40 transition-all duration-300">
                <div className="text-3xl font-bold text-[#E6A05C] mb-2">15+</div>
                <div className="text-sm text-gray-700 font-semibold">Modern Fleet Vehicles</div>
              </div>
              <div className="bg-gradient-to-br from-white to-[#FAF7F3] rounded-xl p-6 shadow-lg border-2 border-[#E6A05C]/20 hover:border-[#E6A05C]/40 transition-all duration-300">
                <div className="text-3xl font-bold text-[#E6A05C] mb-2">20+</div>
                <div className="text-sm text-gray-700 font-semibold">Expert Team Members</div>
              </div>
            </div>
          </div>

          {/* Right - Image Grid with Truck Photos */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="grid grid-cols-2 gap-4">
              {/* Large Image - Real Truck */}
              <div className="col-span-2 h-64 rounded-2xl overflow-hidden relative group shadow-xl">
                <img 
                  src="/truck3.jpg" 
                  alt="Vitanza Logistics Fleet" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D2436]/80 to-transparent flex items-end">
                  <div className="p-6 w-full">
                    <img src="/vitanza-logo.png" alt="Vitanza" className="h-16 w-16 object-contain mb-2" />
                    <h4 className="text-white font-bold text-xl">Professional Fleet</h4>
                    <p className="text-white/90 text-sm">GPS tracked & well-maintained</p>
                  </div>
                </div>
              </div>
              
              {/* Small Images */}
              <div className="h-48 bg-gradient-to-br from-[#E6A05C] to-[#D4894B] rounded-2xl overflow-hidden relative group shadow-lg">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                  <MapPin className="text-white mb-3 group-hover:scale-110 transition-transform duration-300" size={48} />
                  <h4 className="text-white font-bold text-lg">USA & Canada</h4>
                  <p className="text-white/90 text-sm">Cross-border specialists</p>
                </div>
              </div>
              
              <div className="h-48 bg-gradient-to-br from-[#1B4B6B] to-[#0D2436] rounded-2xl overflow-hidden relative group shadow-lg">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                  <Shield className="text-[#E6A05C] mb-3 group-hover:scale-110 transition-transform duration-300" size={48} />
                  <h4 className="text-white font-bold text-lg">DOT Certified</h4>
                  <p className="text-white/90 text-sm">Fully insured</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h3 className="text-3xl md:text-4xl font-bold text-[#0D2436] mb-4">
            Why Choose Vitanza Logistics?
          </h3>
          <p className="text-lg text-gray-600">
            We combine experience, technology, and dedication to deliver exceptional service.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {whyChooseUs.map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <div
                key={index}
                className={`text-center group transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-white to-[#FAF7F3] rounded-2xl flex items-center justify-center shadow-lg border-2 border-[#E6A05C]/20 group-hover:border-[#E6A05C]/50 transition-all duration-300 group-hover:scale-110">
                    <Icon className="text-[#E6A05C]" size={36} />
                  </div>
                  <div className="absolute inset-0 bg-[#E6A05C] rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                <h4 className="text-xl font-bold text-[#0D2436] mb-3 group-hover:text-[#E6A05C] transition-colors duration-300">
                  {item.title}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
